import React, { ChangeEvent, useCallback, useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";

// Components
import HospitalCard from "~/components/cards/hospitals/HospitalCard";
// import CheckboxTab from '~/components/_uikit/CheckboxTab'
import Pagination from "~/components/_uikit/Pagination/Pagination";
import NoFountEntity from "~/components/common/NoFoundEntity/NoFountEntity";
import Search from "~/components/_uikit/Search/Search";
// Styles
import s from "~/views/appointment/hospitals/HospitalPage.module.scss";
import styles from "~/App.module.scss";
// Hooks
import { useStore } from "~/hooks/useStore";
// Store
import RootStore from "~/store/RootStore";
// Utils
import { goToHelpPage } from "~/utils/helpers";
import ROUTES from "~/utils/routes";
import { logEvent } from "~/utils/amplitude";

import { IHospital } from "~/types/hospital";

interface Props {}

const HospitalPage: React.FC<Props> = observer(({}) => {
  const store: RootStore = useStore();
  const hospitalContainerRef = useRef(null);
  const firstRenderRef = useRef(true);
  const searchValue = store.hospitalStore.currentSearchValue;
  const setSearchValue = (value: string) =>
    store.hospitalStore.setCurrentSearchValue(value);

  const currentlyDisplayedData =
    store.hospitalStore.currentlyDisplayedHospitals;

  const navigate = useNavigate();

  useEffect(() => {
    logEvent("Э/ Страница с списком больниц", {});
  }, []);

  const handleClickHospital = useCallback(
    (hospital: IHospital) => {
      store.hospitalStore.setCurrentHospital(hospital.idHospital);

      logEvent("Д/ Нажал плашку больницы", {
        "Название больницы": hospital.organisationName,
        Тип: "ГОС",
      });

      store.appointmentStepsStore.setCurrentStepForDisabling(
        store.cityStore.isMoscowRegion ? 2 : 1
      );

      store.cityStore.isMoscowRegion
        ? navigate(ROUTES.DATES.PATHNAME)
        : navigate(ROUTES.SPECIALTIES.PATHNAME, { replace: true });
    },
    [
      navigate,
      store.hospitalStore,
      store.appointmentStepsStore,
      store.cityStore.isMoscowRegion,
    ]
  );

  const changePage = useCallback(
    (page: number) => {
      store.hospitalStore.pagination.setCurrentPage(page);
    },
    [store.hospitalStore]
  );

  const handleSearch = (searchQuery: string) => {
    logEvent("Д/ Нажал кнопку Найти / Страница с списком больниц", {
      "Введенные данные": searchQuery,
    });

    store.hospitalStore.setQuery(searchQuery);
    store.hospitalStore.pagination.setCurrentPage(1);
  };

  const handleSearchBlur = (text: string) => {
    logEvent("Д/ Ввел поиск больницы / Страница с списком больниц", {
      "Введенные данные": text,
    });
  };

  // ========================================Effects-START=======================================
  useEffect(() => {
    return () => {
      store.hospitalStore.setCurrentSearchValue("");
      store.hospitalStore.setQuery("");
    };
  }, [store.hospitalStore]);

  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    if (hospitalContainerRef.current) {
      (hospitalContainerRef.current as HTMLElement).scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [store.hospitalStore.pagination.currentPage, hospitalContainerRef]);

  useEffect(() => {
    store.hospitalStore.pagination.setItemsDisplayedOnPage(7);
  }, [store.hospitalStore]);

  useEffect(() => {
    if (
      store.hospitalStore.hospitalList.length === 0 &&
      store.cityStore.currentCity?.area_code &&
      store.patientStore.currentPatient?.idUserData
    ) {
      store.hospitalStore.fetchHospitals({
        id_region: store.cityStore.currentCity.area_code,
        id_user_data: store.patientStore.currentPatient.idUserData,
        ...(store.specialityStore.currentSpeciality && {
          id_specialty: store.specialityStore.currentSpeciality.idSpecialty,
        }),
        ...(store.specialistStore.currentSpecialist && {
          id_doctor: store.specialistStore.currentSpecialist.idDoctor,
        }),
      });
    } else if (
      !(
        store.cityStore.currentCity?.area_code &&
        store.patientStore.currentPatient?.idUserData
      )
    ) {
      navigate(ROUTES.LOGIN.PATHNAME, { replace: true });
    }
  }, [
    store.hospitalStore,
    store.patientStore,
    store.cityStore,
    store.cityStore.currentCity,
    store.specialityStore.currentSpeciality,
    store.specialistStore.currentSpecialist,
    navigate,
  ]);
  // ========================================Effects-END=======================================

  // eslint-disable-next-line
  const governmentControlCallback = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    store.hospitalStore.toggleGovernmentFilter();
  };

  // eslint-disable-next-line
  const privateControlCallback = (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
    store.hospitalStore.togglePrivateFilter();
  };

  return (
    <>
      {!store.cityStore.isMoscowRegion && (
        <div className={s.layoutSearch}>
          <div className={s.layoutSearchBar}>
            <Search
              value={searchValue}
              placeholder="Поиск медицинского учреждения"
              onChange={setSearchValue}
              onSubmit={handleSearch}
              onBlur={handleSearchBlur}
            />
          </div>
          {/* <div className={s.layoutSearchFilters}>
            <CheckboxTab
              label='Государственные'
              name='government'
              onChange={governmentControlCallback}
              value={store.hospitalStore.hospitalsFilter.government}
            />
            <CheckboxTab
              label='Частные'
              name='private'
              onChange={privateControlCallback}
              value={store.hospitalStore.hospitalsFilter.private}
            />
          </div> */}
        </div>
      )}

      <div className="screen-container">
        {!!(!store.systemStore.loading && currentlyDisplayedData.length) && (
          <>
            <div className={styles.appointmentHeadingWrap}>
              <h2 className="headline-2">Доступные организации для записи:</h2>
            </div>

            <div ref={hospitalContainerRef} className={s.layoutCards}>
              {currentlyDisplayedData.map((hospital) => {
                return (
                  <HospitalCard
                    key={hospital.idHospital}
                    hospitalData={hospital}
                    onClick={() => handleClickHospital(hospital)}
                  />
                );
              })}
            </div>
          </>
        )}

        {!!(!store.systemStore.loading && !currentlyDisplayedData.length) && (
          <div className={s.hospitalNoFoundData}>
            <NoFountEntity
              title="Нет подходящих больниц"
              subtitle="Не нашли больницу? Расскажите нам об этом!"
              buttonText="Не нашли больницу"
              onClick={goToHelpPage}
            />
          </div>
        )}

        {!store.hospitalStore.pagination.noPagesOrSinglePage && (
          <div className={s.layoutCardsPagination}>
            <Pagination
              count={store.hospitalStore.pagination.amountOfPages}
              currentPage={store.hospitalStore.pagination.currentPage}
              onChange={changePage}
              canGoPreviousPage={
                store.hospitalStore.pagination.canGoPreviousPage
              }
              canGoNextPage={store.hospitalStore.pagination.canGoNextPage}
              goPreviousPage={store.hospitalStore.pagination.goPreviousPage}
              goNextPage={store.hospitalStore.pagination.goNextPage}
            />
          </div>
        )}
      </div>
    </>
  );
});

export default HospitalPage;
